import {ActionIcon, Flex, Space} from "@mantine/core";
import {addDays, addMinutes, isAfter, isBefore, subDays, subMinutes} from "@ots-frontend/ots-data";
import {useCallback, useState} from "react";
import {FaCaretLeft, FaCaretRight} from "react-icons/fa";
import {DateInput, DateInputProps} from "../DateInput/DateInput";
import {BaseSelect} from "../../select/base-select";
import {useTranslation} from "react-i18next";

export interface IncrementalDateInputProps extends Omit<DateInputProps, "allowDeselect" | 'minDate' | 'maxDate'> {
  // props here
  minDate?: Date | number
  maxDate?: Date | number
  showIncrement?: boolean
}

export const IncrementalDateInput = (props: IncrementalDateInputProps) => {
  const {
    defaultValue,
    onChange,
    minDate,
    maxDate,
    showIncrement = false,
    ...rest
  } = props;

  const {t} = useTranslation(["common"])

  const [internalValue, setInternalValue] = useState<Date | null | undefined>(
    defaultValue
  );

  const [increment, setIncrement] = useState<number>(showIncrement ? 5 : 1);

  const handleChange = useCallback((date: Date | null) => {
    if (date) {
      if (onChange && date) {
        onChange(date);
      }

      setInternalValue(date);
    }
  }, [onChange])

  const decrementDate = useCallback(() => {
    let newValue: Date | undefined = undefined

    if (internalValue) {
      if (showIncrement) {
        newValue = subMinutes(internalValue, increment)
      } else {
        newValue = subDays(internalValue, increment)
      }

      if (minDate && isBefore(newValue, minDate)) {
        newValue = new Date(minDate)
      }
    }

    if (newValue) {
      setInternalValue(newValue);
      handleChange(newValue);
    }
  }, [internalValue, showIncrement, minDate, increment, handleChange])

  const incrementDate = useCallback(() => {
    let newValue: Date | undefined = undefined

    if (internalValue) {
      if (showIncrement) {
        newValue = addMinutes(internalValue, increment)
      } else {
        newValue = addDays(internalValue, increment)
      }

      if (maxDate && isAfter(newValue, maxDate)) {
        newValue = new Date(maxDate)
      }
    }

    if (newValue) {
      setInternalValue(newValue);
      handleChange(newValue);
    }
  }, [internalValue, handleChange, increment, maxDate])

  const onIncrementChange = useCallback((value: string | null) => {
    if (value) {
      const numValue = Number(value);
      if (!isNaN(numValue)) {
        setIncrement(numValue);
      }
    }
  }, [])

  return (
    <Flex
      mih={50}
      justify="center"
      align="center"
      direction="row"
      wrap="wrap"
    >
      <ActionIcon onClick={decrementDate}>
        <FaCaretLeft size="1rem"/>
      </ActionIcon>
      <Space w="xs"/>
      <DateInput
        allowDeselect={false}
        value={internalValue}
        onChange={(d) => handleChange(d)}
        minDate={minDate ? new Date(minDate) : undefined}
        maxDate={maxDate ? new Date(maxDate) : undefined}
        {...rest}
      />
      {showIncrement &&
        <>
          <Space w="xs"/>
          <BaseSelect
            w={150}
            placeholder={t<string>("common.input.increment", "Increment")}
            defaultValue={"5"}
            onChange={onIncrementChange}
            data={[
              {value: "1", label: "1 Minute"},
              {value: "5", label: "5 Minutes"},
              {value: "10", label: "10 Minutes"},
            ]}
            withinPortal={true}
          />
        </>
      }
      <Space w="xs"/>
      <ActionIcon onClick={incrementDate}>
        <FaCaretRight size="1rem"/>
      </ActionIcon>
    </Flex>
  );
};
